import React, { Component } from "react";
import ApexCharts from "apexcharts";
import "./evs-detail-score-analysis.css";
import axios from '../../../axios';
import apiError from '../../../error';
import { formatDurationFromSeconds } from '../../../function';
const FILLED_COLOR = "#DCE6F6";
const UNFILLED_COLOR = "#F0F0F0";
const LABEL_COLOR = "#4F81BD";
const EVS_TREND_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"];
const TREND_LINE_COLOR = "#A78BFA";
const TREND_FILL_COLOR = "#DDD6FE";
// Repeat Offender donut & Campaign Summary: color palettes (labels come from API)
const REPEAT_OFFENDER_COLORS = [
	"#2d91fb",
	"#feca29",
	"#4fa648",
	"#9747ff",
	"#ffd7d4",
	"#c0dffe",
	"#ff9f7f",
	"#9b8cff",
	"#34d399",
	"#fbbf24",
	"#f97373",
	"#93c5fd",
];
const SUMMARY_OFFENDER_COLORS = [
	"#2d91fb",
	"#feca29",
	"#4fa648",
	"#9747ff",
	"#ffd7d4",
	"#c0dffe",
	"#ff9f7f",
	"#9b8cff",
	"#34d399",
	"#fbbf24",
	"#f97373",
	"#93c5fd",
];
const REPEAT_CONNECTOR_COLOR = "#d1d5db";
const REPEAT_DOT_RADIUS = 2;
const REPEAT_CHART_HEIGHT = 110;
const REPEAT_VIEWBOX = { w: 400, h: REPEAT_CHART_HEIGHT };
const DEG = Math.PI / 180;
/* Apex donut inner hole as % of radius — higher = thinner ring */
const REPEAT_DONUT_INNER_PERCENT = "84%";
/* Match ApexCharts donut: center and outer radius (radialSize ≈ defaultSize/2.05) */
const REPEAT_CX = 200;
const REPEAT_CY = REPEAT_CHART_HEIGHT / 2;
const REPEAT_R = Math.round(REPEAT_CHART_HEIGHT / 2.05) - 1; /* Apex radialSize so dot sits on donut ring */
/* Keep connector and labels outside donut */
/* D3-style: radial out from slice to outer arc, then horizontal to label. Outer radius = donut R + this. */
const REPEAT_OUTER_RADIUS_OFFSET = 14;
/* Approx width for 9px font so line ends at label edge (no cut-through, no gap) */
const getRepeatLabelWidth = (text) => Math.ceil((text?.length ?? 0) * 5.2);
/* Min vertical spacing between labels on same side to avoid overlap */
const REPEAT_MIN_LABEL_DY = 12;
/* Padding to push labels 10px inside (towards the donut) */
const REPEAT_LABEL_INSIDE_PADDING = 10;

export default class EvsDetailScoreAnalysis extends Component {
	state = {
		chart: null,
		trendChart: null,
		repeatChart: null,
		summaryChart: null,
		evsScoreCategories: [],
		evsScoreValues: [],
		evsTrendValues: Array.isArray(EVS_TREND_MONTHS) ? Array(EVS_TREND_MONTHS.length).fill(0) : [],
		repeatOffenderSeries: [],
		repeatOffenderLabels: [],
		campaignSummarySeries: [],
		campaignSummaryLabels: [],
		breachCount: 0,
		minimumBreachTime: 0,
		totalCampaigns: 0,
	};

	chartId = "evs-detail-score-chart";
	trendChartId = "evs-detail-trend-chart";
	repeatChartId = "evs-detail-repeat-chart";
	summaryChartId = "evs-detail-summary-chart";

	_isMounted = false;

	getTargetId = () => {
		const { evs } = this.props;
		// Analytics API expects the employee/target id (not the EVS record id)
		return evs?.target?.id ?? evs?.targetId ?? null;
	};

	getAnalyticsPayload = (rawData, responseKey) => {
		if (!rawData || typeof rawData !== "object") return null;

		// Most EVS APIs are wrapped like: { success, message, data: { ... } }
		const root = rawData.data && typeof rawData.data === "object" ? rawData.data : rawData;

		if (responseKey && root && Object.prototype.hasOwnProperty.call(root, responseKey)) {
			return root[responseKey];
		}

		return root;
	};

	handleEvsScoreResponse = (payload) => {
		if (!payload || typeof payload !== "object") return;

		const evsByAttackTypes = Array.isArray(payload.evsByAttackTypes) ? payload.evsByAttackTypes : [];

		const categoriesFromApi = evsByAttackTypes.map((item) => item.attackTypeName || "Unknown");
		const scoresFromApi = evsByAttackTypes.map((item) => {
			const n = Number(item.evsPercentage);
			if (!Number.isFinite(n)) return 0;
			if (n < 0) return 0;
			if (n > 100) return 100;
			return n;
		});

		const hasServerData = categoriesFromApi.length > 0 && scoresFromApi.length > 0;
		const categories = hasServerData ? categoriesFromApi : [];
		const scores = hasServerData ? scoresFromApi : [];

		const avgRaw = payload.averageEvsPercentage;
		const avg =
			avgRaw === null || avgRaw === undefined
				? 0
				: Math.max(0, Math.min(100, Number(avgRaw) || 0));

		this.setState(
			{
				evsScoreCategories: categories,
				evsScoreValues: scores,
			},
			() => {
				const hasDataNow =
					Array.isArray(this.state.evsScoreValues) &&
					this.state.evsScoreValues.length > 0;
				const scoreEl = document.querySelector("#" + this.chartId);
				const currentChart = this.state.chart;

				if (hasDataNow && scoreEl) {
					if (currentChart) {
						currentChart.updateOptions(this.buildOptions(categories, scores));
					} else {
						const chart = new ApexCharts(
							scoreEl,
							this.buildOptions(categories, scores)
						);
						chart.render();
						this.setState({ chart });
					}
				} else if (!hasDataNow && currentChart) {
					try {
						currentChart.destroy();
					} catch (e) {
						// Ignore DOM errors during destroy
					}
					this.setState({ chart: null });
				}
			}
		);

		if (typeof this.props.onAverageEvsChange === "function") {
			this.props.onAverageEvsChange(avg);
		}
	};

	handleEvsTrendResponse = (payload) => {
		const months = Array.isArray(EVS_TREND_MONTHS) ? EVS_TREND_MONTHS.length : 0;
		const base = months > 0 ? Array(months).fill(0) : [];
		const items = Array.isArray(payload) ? payload : [];

		items.forEach((item) => {
			const monthIndex = Number(item.month);
			const valueRaw = item.evsPercentage;
			if (!Number.isFinite(monthIndex)) return;
			const idx = monthIndex - 1;
			if (idx < 0 || idx >= base.length) return;
			const n = Number(valueRaw);
			if (!Number.isFinite(n)) return;
			const clamped = Math.max(0, Math.min(100, n));
			base[idx] = clamped;
		});

		this.setState(
			{
				evsTrendValues: base,
			},
			() => {
				const hasDataNow =
					Array.isArray(this.state.evsTrendValues) &&
					this.state.evsTrendValues.some((v) => Number(v) > 0);
				const trendEl = document.querySelector("#" + this.trendChartId);
				const currentChart = this.state.trendChart;

				if (hasDataNow && trendEl) {
					if (currentChart) {
						currentChart.updateOptions(this.buildTrendOptions());
					} else {
						const trendChart = new ApexCharts(
							trendEl,
							this.buildTrendOptions()
						);
						trendChart.render();
						this.setState({ trendChart });
					}
				} else if (!hasDataNow && currentChart) {
					try {
						currentChart.destroy();
					} catch (e) {
						// Ignore DOM errors during destroy
					}
					this.setState({ trendChart: null });
				}
			}
		);
	};

	handleRepeatOffendersResponse = (payload) => {
		const items = Array.isArray(payload) ? payload : [];

		// Build labels and series directly from API (no hardcoded labels, ignore zero/invalid hacks)
		const labels = [];
		const series = [];

		items.forEach((item) => {
			const rawName = (item && item.attackTypeName) || "";
			const name = String(rawName).trim();
			if (!name) return;

			const n = Number(item.hacks);
			if (!Number.isFinite(n) || n <= 0) return;

			labels.push(name);
			series.push(n);
		});

		this.setState(
			{
				repeatOffenderSeries: series,
				repeatOffenderLabels: labels,
			},
			() => {
				const hasDataNow =
					Array.isArray(this.state.repeatOffenderSeries) &&
					this.state.repeatOffenderSeries.some((v) => Number(v) > 0);
				const repeatEl = document.querySelector("#" + this.repeatChartId);
				const currentChart = this.state.repeatChart;

				if (hasDataNow && repeatEl) {
					if (currentChart) {
						currentChart.updateOptions(this.buildRepeatOptions());
					} else {
						const repeatChart = new ApexCharts(
							repeatEl,
							this.buildRepeatOptions()
						);
						repeatChart.render();
						this.setState({ repeatChart });
					}
				} else if (!hasDataNow && currentChart) {
					try {
						currentChart.destroy();
					} catch (e) {
						// Ignore DOM errors during destroy
					}
					this.setState({ repeatChart: null });
				}
			}
		);
	};

	handleCampaignSummaryResponse = (payload) => {
		const items = Array.isArray(payload) ? payload : [];

		const labels = [];
		const series = [];

		items.forEach((item) => {
			const rawName = (item && item.attackTypeName) || "";
			const name = String(rawName).trim();
			if (!name) return;

			const n = Number(item.simulationCount);
			if (!Number.isFinite(n) || n <= 0) return;

			labels.push(name);
			series.push(n);
		});

		this.setState(
			{
				campaignSummarySeries: series,
				campaignSummaryLabels: labels,
			},
			() => {
				const hasDataNow =
					Array.isArray(this.state.campaignSummarySeries) &&
					this.state.campaignSummarySeries.some((v) => Number(v) > 0);
				const summaryEl = document.querySelector("#" + this.summaryChartId);
				const currentChart = this.state.summaryChart;

				if (hasDataNow && summaryEl) {
					if (currentChart) {
						currentChart.updateOptions(this.buildSummaryOptions());
					} else {
						const summaryChart = new ApexCharts(
							summaryEl,
							this.buildSummaryOptions()
						);
						summaryChart.render();
						this.setState({ summaryChart });
					}
				} else if (!hasDataNow && currentChart) {
					try {
						currentChart.destroy();
					} catch (e) {
						// Ignore DOM errors during destroy
					}
					this.setState({ summaryChart: null });
				}
			}
		);
	};

	handleBreachCountResponse = (payload) => {
		const n = Number(payload);
		const value = Number.isFinite(n) && n >= 0 ? n : 0;
		this.setState({ breachCount: value });
	};

	handleMinimumBreachTimeResponse = (payload) => {
		const n = Number(payload);
		const value = Number.isFinite(n) && n >= 0 ? n : 0;
		this.setState({ minimumBreachTime: value });
	};

	fetchAnalytics = (graphType, responseKey) => {
		const targetId = this.getTargetId();
		if (!targetId) return;

		const params = { graphType };
		// Optional: attackTypeIds must be sent as an array
		if (Array.isArray(this.props.attackTypeIds) && this.props.attackTypeIds.length) {
			params.attackTypeIds = this.props.attackTypeIds;
		}

		axios
			.get(`/api/evs/analytics/${targetId}`, { params })
			.then((res) => {
				if (!this._isMounted) return;
				const rootPayload = this.getAnalyticsPayload(res?.data);
				const payload = this.getAnalyticsPayload(res?.data, responseKey);
				if (graphType === "evs-score") {
					this.handleEvsScoreResponse(payload);
				} else if (graphType === "evs-trend") {
					this.handleEvsTrendResponse(payload);
				} else if (graphType === "repeat-offenders") {
					this.handleRepeatOffendersResponse(payload);
				} else if (graphType === "campaign-summary") {
					const totalCampaignsRaw = rootPayload?.totalCampaigns;
					const totalCampaigns = Number.isFinite(Number(totalCampaignsRaw))
						? Math.max(0, Number(totalCampaignsRaw))
						: 0;
					if (this.state.totalCampaigns !== totalCampaigns) {
						this.setState({ totalCampaigns });
					}
					this.handleCampaignSummaryResponse(Array.isArray(rootPayload?.campaignSummary) ? rootPayload.campaignSummary : payload);
				} else if (graphType === "breach-count") {
					this.handleBreachCountResponse(payload);
				} else if (graphType === "minimum-breach-time") {
					this.handleMinimumBreachTimeResponse(payload);
				}
			})
			.catch((err) => apiError(err));
	};

	fetchAllAnalytics = () => {
		const targetId = this.getTargetId();
		if (!targetId) return;

		const graphs = [
			{ graphType: "evs-score", responseKey: "evsScore" },
			{ graphType: "evs-trend", responseKey: "evsTrend" },
			{ graphType: "repeat-offenders", responseKey: "repeatOffenders" },
			{ graphType: "campaign-summary", responseKey: "campaignSummary" },
			{ graphType: "breach-count", responseKey: "breachCount" },
			{ graphType: "minimum-breach-time", responseKey: "minimumBreachTime" },
		];

		graphs.forEach(({ graphType, responseKey }) => this.fetchAnalytics(graphType, responseKey));
	};

	buildOptions = (categoriesArg, scoresArg) => {
		const scores = Array.isArray(scoresArg) && scoresArg.length ? scoresArg : [];
		const safeScores = scores.map((s) => {
			const n = Number(s);
			if (!Number.isFinite(n)) return 0;
			if (n < 0) return 0;
			if (n > 100) return 100;
			return n;
		});

		const hasData = safeScores.length > 0;
		const baseScores = hasData ? safeScores : [0];
		const baseRemaining = hasData ? safeScores.map((s) => 100 - s) : [100];
		const categories = Array.isArray(categoriesArg) && categoriesArg.length ? categoriesArg : [];
		const baseCategories = hasData ? categories : [""];

		return {
			series: [
				{ name: "Score", data: baseScores },
				{ name: "Remaining", data: baseRemaining },
			],
			chart: {
				type: "bar",
				stacked: true,
				height: 240,
				toolbar: { show: false },
				zoom: { enabled: false },
				background: "transparent",
			},
			colors: [FILLED_COLOR, UNFILLED_COLOR],
			states: {
				normal: {
					filter: { type: "none" },
				},
				hover: {
					filter: { type: "none" },
				},
				active: {
					filter: { type: "none" },
				},
			},
			plotOptions: {
				bar: {
					horizontal: false,
					columnWidth: "50px",
					borderRadius: 4,
					dataLabels: {
						position: "bottom",
						hideOverflowingLabels: false,
					},
				},
			},
			dataLabels: {
				enabled: true,
				formatter: (val, opts) => (opts.seriesIndex === 0 ? val + "%" : ""),
				style: {
					fontSize: "10px",
					colors: [LABEL_COLOR],
				},
				dropShadow: { enabled: false },
			},
			legend: { show: false },
			xaxis: {
				categories: baseCategories,
				labels: {
					style: {
						colors: "#000000",
						fontSize: "10px",
					},
				},
				axisBorder: { show: true },
				axisTicks: { show: false },
			},
			yaxis: {
				max: 100,
				tickAmount: 5,
				labels: {
					formatter: (val) => Number(val).toFixed(2),
					offsetX: -8,
					style: {
						colors: "#000000",
						fontSize: "10px",
					},
				},
				axisBorder: { show: false },
				axisTicks: { show: false },
				title: { show: false },
			},
			grid: {
				borderColor: "#e5e7eb",
				strokeDashArray: 4,
				xaxis: { lines: { show: false } },
				yaxis: { lines: { show: true } },
				padding: { top: 0, right: 10, bottom: 0, left: 8 },
			},
			stroke: {
				width: 0,
				show: false,
			},
		};
	};

	buildTrendOptions = () => {
		const data =
			Array.isArray(this.state.evsTrendValues) && this.state.evsTrendValues.length
				? this.state.evsTrendValues
				: (Array.isArray(EVS_TREND_MONTHS) ? Array(EVS_TREND_MONTHS.length).fill(0) : []);

		return {
			series: [{ name: "EVS Trend", data }],
			chart: {
				type: "area",
				height: 240,
				toolbar: { show: false },
				zoom: { enabled: false },
				background: "transparent",
			},
			colors: [TREND_LINE_COLOR],
			stroke: {
				curve: "smooth",
				width: 2.5,
				colors: [TREND_LINE_COLOR],
			},
			fill: {
				type: "solid",
				colors: [TREND_FILL_COLOR],
				opacity: 0.5,
			},
			dataLabels: { enabled: false },
			legend: { show: false },
			xaxis: {
				categories: EVS_TREND_MONTHS,
				labels: {
					style: {
						colors: "#000000",
						fontSize: "10px",
					},
				},
				axisBorder: { show: true },
				axisTicks: { show: false },
			},
			yaxis: {
				min: 0,
				max: 100,
				tickAmount: 5,
				labels: {
					formatter: (val) => Number(val).toFixed(2),
					offsetX: -8,
					style: {
						colors: "#000000",
						fontSize: "10px",
					},
				},
				axisBorder: { show: false },
				axisTicks: { show: false },
				title: { show: false },
			},
			grid: {
				borderColor: "#e5e7eb",
				strokeDashArray: 4,
				xaxis: { lines: { show: false } },
				yaxis: { lines: { show: true } },
				padding: { top: 0, right: 8, bottom: 0, left: 8 },
			},
			tooltip: { enabled: true },
		};
	};

	buildRepeatOptions = () => {
		const rawSeries = Array.isArray(this.state.repeatOffenderSeries)
			? this.state.repeatOffenderSeries
			: [];
		const rawLabels = Array.isArray(this.state.repeatOffenderLabels)
			? this.state.repeatOffenderLabels
			: [];

		const activeIndices = rawSeries
			.map((v, i) => (Number(v) > 0 ? i : -1))
			.filter((i) => i >= 0 && rawLabels[i]);

		if (!activeIndices.length) {
			// Should not be called when there is no data; guard anyway
			return {
				series: [],
				chart: {
					type: "donut",
					height: REPEAT_CHART_HEIGHT,
					background: "transparent",
				},
				labels: [],
				legend: { show: false },
				dataLabels: { enabled: false },
				stroke: { width: 0 },
				tooltip: { enabled: true },
			};
		}

		const series = activeIndices.map((i) => rawSeries[i]);
		const labels = activeIndices.map((i) => rawLabels[i]);
		const colors = activeIndices.map(
			(_, idx) => REPEAT_OFFENDER_COLORS[idx % REPEAT_OFFENDER_COLORS.length]
		);

		return {
			series,
			chart: {
				type: "donut",
				height: REPEAT_CHART_HEIGHT,
				background: "transparent",
			},
			labels,
			colors,
			states: {
				normal: { filter: { type: "none" } },
				hover: { filter: { type: "none" } },
				active: { filter: { type: "none" } },
			},
			legend: { show: false },
			plotOptions: {
				pie: {
					dataLabels: { offset: 0 },
					donut: {
						size: REPEAT_DONUT_INNER_PERCENT,
						labels: {
							show: true,
							name: { show: false },
							value: { show: false },
							total: {
								show: true,
								label: "Total",
								fontSize: "9px",
								fontWeight: 400,
								color: "#000000",
								formatter: (w) =>
									String(w.globals.seriesTotals.reduce((a, b) => a + b, 0)),
							},
						},
					},
				},
			},
			dataLabels: { enabled: false },
			stroke: { width: 0 },
			tooltip: { enabled: true },
		};
	};

	getDonutLeaderPaths = (data, labelNames) => {
		const total = data.reduce((a, b) => a + b, 0) || 1;
		const cx = REPEAT_CX;
		const cy = REPEAT_CY;
		const r = REPEAT_R;
		const outerR = r + REPEAT_OUTER_RADIUS_OFFSET;
		const leftLabelX = 10 + REPEAT_LABEL_INSIDE_PADDING;
		const rightLabelX = REPEAT_VIEWBOX.w - 10 - REPEAT_LABEL_INSIDE_PADDING;
		// ApexCharts: startAngle 0 = top (12 o'clock), clockwise. Mid angle per segment.
		let apexDeg = 0;
		const apexMidAngles = data.map((val) => {
			const mid = apexDeg + (360 * val) / total / 2;
			apexDeg += (360 * val) / total;
			return mid;
		});
		// D3-style polyline: [slice edge] -> [outer arc point] -> [line end]; label placed at labelX so line doesn't strike through.
		const raw = apexMidAngles.map((apexDegVal, i) => {
			const rad = (apexDegVal - 90) * DEG;
			const dotX = cx + r * Math.cos(rad);
			const dotY = cy + r * Math.sin(rad);
			const outerX = cx + outerR * Math.cos(rad);
			const outerY = cy + outerR * Math.sin(rad);
			const isRight = dotX >= cx;
			const labelX = isRight ? rightLabelX : leftLabelX;
			return { i, dotX, dotY, outerX, outerY, labelX, isRight, rad };
		});
		// Resolve overlapping label Y: set initial labelY = outerY, then cascade so each label is at least REPEAT_MIN_LABEL_DY below the previous on same side.
		const nudgeSameSide = (items) => {
			items.forEach((d) => { d.labelY = d.outerY; });
			items.sort((a, b) => a.labelY - b.labelY);
			for (let k = 1; k < items.length; k++) {
				const prevY = items[k - 1].labelY;
				if (items[k].labelY - prevY < REPEAT_MIN_LABEL_DY) {
					items[k].labelY = prevY + REPEAT_MIN_LABEL_DY;
				}
			}
		};
		const rightItems = raw.filter((d) => d.isRight);
		const leftItems = raw.filter((d) => !d.isRight);
		nudgeSameSide(rightItems);
		nudgeSameSide(leftItems);
		raw.forEach((d) => {
			const side = d.isRight ? rightItems : leftItems;
			const idx = side.findIndex((x) => x.i === d.i);
			d.labelY = idx >= 0 ? side[idx].labelY : d.outerY;
		});
		const paths = [];
		const labels = [];
		raw.forEach((d) => {
			const n = String(data[d.i]).padStart(2, "0");
			const name = labelNames[d.i] != null ? labelNames[d.i] : "";
			const text = d.isRight ? `${name} (${n})` : `(${n}) ${name}`;
			const w = getRepeatLabelWidth(text);
			// End line at label edge: right = left edge (x - w); left = right edge (x + w) so line doesn't cut through
			const lineEndX = d.isRight ? d.labelX - w : d.labelX + w;
			const pathD =
				`M ${d.dotX.toFixed(1)} ${d.dotY.toFixed(1)}` +
				` L ${d.outerX.toFixed(1)} ${d.outerY.toFixed(1)}` +
				` L ${lineEndX} ${d.labelY.toFixed(1)}`;
			paths.push({ d: pathD, key: d.i, dotX: d.dotX, dotY: d.dotY });
			labels.push({
				key: d.i,
				x: d.labelX,
				y: d.labelY,
				text,
				anchor: d.isRight ? "end" : "start",
			});
		});
		return { paths, labels };
	};

	getRepeatLeaderPaths = () => {
		const data = Array.isArray(this.state.repeatOffenderSeries)
			? this.state.repeatOffenderSeries
			: [];
		const labelNames = Array.isArray(this.state.repeatOffenderLabels)
			? this.state.repeatOffenderLabels
			: [];

		if (!data.length || !labelNames.length) return { paths: [], labels: [] };

		return this.getDonutLeaderPaths(data, labelNames);
	};
	getSummaryLeaderPaths = () => {
		const data = Array.isArray(this.state.campaignSummarySeries)
			? this.state.campaignSummarySeries
			: [];
		const labelNames = Array.isArray(this.state.campaignSummaryLabels)
			? this.state.campaignSummaryLabels
			: [];

		if (!data.length || !labelNames.length) return { paths: [], labels: [] };

		return this.getDonutLeaderPaths(data, labelNames);
	};

	getRepeatTotal = () => {
		const arr = Array.isArray(this.state.repeatOffenderSeries)
			? this.state.repeatOffenderSeries
			: [];
		return arr.reduce((sum, v) => {
			const n = Number(v);
			return sum + (Number.isFinite(n) && n > 0 ? n : 0);
		}, 0);
	};

	getSummaryTotal = () => {
		const arr = Array.isArray(this.state.campaignSummarySeries)
			? this.state.campaignSummarySeries
			: [];
		return arr.reduce((sum, v) => {
			const n = Number(v);
			return sum + (Number.isFinite(n) && n > 0 ? n : 0);
		}, 0);
	};

	buildSummaryOptions = () => {
		const series = Array.isArray(this.state.campaignSummarySeries)
			? this.state.campaignSummarySeries
			: [];
		const labels = Array.isArray(this.state.campaignSummaryLabels)
			? this.state.campaignSummaryLabels
			: [];
		const colors = labels.map(
			(_, idx) => SUMMARY_OFFENDER_COLORS[idx % SUMMARY_OFFENDER_COLORS.length]
		);

		return {
			series,
			chart: {
				type: "donut",
				height: REPEAT_CHART_HEIGHT,
				background: "transparent",
			},
			labels,
			colors,
			states: {
				normal: { filter: { type: "none" } },
				hover: { filter: { type: "none" } },
				active: { filter: { type: "none" } },
			},
			legend: { show: false },
			plotOptions: {
				pie: {
					dataLabels: { offset: 0 },
					donut: {
						size: REPEAT_DONUT_INNER_PERCENT,
						labels: {
							show: true,
							name: { show: false },
							value: { show: false },
							total: {
								show: true,
								label: "Total",
								fontSize: "9px",
								fontWeight: 400,
								color: "#000000",
								formatter: (w) =>
									String(w.globals.seriesTotals.reduce((a, b) => a + b, 0)),
							},
						},
					},
				},
			},
			dataLabels: { enabled: false },
			stroke: { width: 0 },
			tooltip: { enabled: true },
		};
	};

	componentDidMount() {
		this._isMounted = true;
		if (!this.props.evs) return;
		// Fetch analytics data for all score-analysis charts
		this.fetchAllAnalytics();
	}

	componentDidUpdate(prevProps) {
		// If drawer switches to a different target, refetch (log only for now)
		const prevTargetId = prevProps?.evs?.target?.id ?? prevProps?.evs?.targetId ?? null;
		const nextTargetId = this.props?.evs?.target?.id ?? this.props?.evs?.targetId ?? null;
		const prevAttackIds = Array.isArray(prevProps?.attackTypeIds) ? prevProps.attackTypeIds.map(String).sort().join(",") : "";
		const nextAttackIds = Array.isArray(this.props?.attackTypeIds) ? this.props.attackTypeIds.map(String).sort().join(",") : "";

		if ((prevTargetId !== nextTargetId && nextTargetId) || prevAttackIds !== nextAttackIds) {
			this.fetchAllAnalytics();
		}
	}

	componentWillUnmount() {
		this._isMounted = false;
		const charts = [
			this.state.chart,
			this.state.trendChart,
			this.state.repeatChart,
			this.state.summaryChart,
		];
		charts.forEach((instance) => {
			if (instance && typeof instance.destroy === "function") {
				try {
					instance.destroy();
				} catch (e) {
					// Ignore chart destroy errors during unmount
				}
			}
		});
	}

	render() {
		const { evs } = this.props;
		const totalCampaigns = Number.isFinite(Number(this.state.totalCampaigns))
			? Number(this.state.totalCampaigns)
			: 0;
		const hasScoreData =
			Array.isArray(this.state.evsScoreValues) &&
			this.state.evsScoreValues.length > 0;
		const hasTrendData =
			Array.isArray(this.state.evsTrendValues) &&
			this.state.evsTrendValues.some((v) => Number(v) > 0);
		const hasRepeatData =
			Array.isArray(this.state.repeatOffenderSeries) &&
			this.state.repeatOffenderSeries.some((v) => Number(v) > 0);
		const hasSummaryData =
			Array.isArray(this.state.campaignSummarySeries) &&
			this.state.campaignSummarySeries.some((v) => Number(v) > 0);

		return (
			<div className="evs-detail-score-analysis">
				{evs && (
					<div className="evs-detail-score-analysis__content">
						<div className="evs-detail-score-analysis__evs-score">
							<h6 className="evs-detail-score-analysis__evs-score-title">EVS Score</h6>
							{hasScoreData ? (
								<div className="evs-detail-score-analysis__evs-score-chart-wrap">
									<div
										id={this.chartId}
										className="evs-detail-score-analysis__evs-score-chart"
									/>
								</div>
							) : (
								<div className="evs-detail-score-analysis__evs-score-no-data">
									No data available
								</div>
							)}
						</div>
						<div className="evs-detail-score-analysis__evs-trend">
							<h6 className="evs-detail-score-analysis__evs-score-title">EVS Trend</h6>
							{hasTrendData ? (
								<div className="evs-detail-score-analysis__evs-trend-chart-wrap">
									<div
										id={this.trendChartId}
										className="evs-detail-score-analysis__evs-trend-chart"
									/>
								</div>
							) : (
								<div className="evs-detail-score-analysis__evs-trend-no-data">
									No data available
								</div>
							)}
						</div>
						<div className="evs-detail-score-analysis__metrics-row">
							<div className="evs-detail-score-analysis__metric-card">
								<h6 className="evs-detail-score-analysis__evs-score-title mb-2">Breach Count</h6>
								<div className="evs-detail-score-analysis__metric-content">
									<span className="evs-detail-score-analysis__metric-value evs-detail-score-analysis__metric-value--breach">
										{this.state.breachCount}
									</span>
									<span className="evs-detail-score-analysis__metric-suffix">
										times out of {totalCampaigns}
									</span>
								</div>
							</div>
							<div className="evs-detail-score-analysis__metric-card">
								<h6 className="evs-detail-score-analysis__evs-score-title mb-2">Minimum Breach Time</h6>
								<div className="evs-detail-score-analysis__metric-content">
									<span className="evs-detail-score-analysis__metric-value evs-detail-score-analysis__metric-value--time">
										{formatDurationFromSeconds(this.state.minimumBreachTime)}
									</span>
								</div>
							</div>
						</div>
							<div className="evs-detail-score-analysis__evs-score-pies">
							<div className="evs-detail-score-analysis__repeat">
								<h6 className="evs-detail-score-analysis__evs-score-title mb-2">Repeat Offender</h6>
								{hasRepeatData ? (
									<div className="evs-detail-score-analysis__repeat-chart-wrap">
										<div
											id={this.repeatChartId}
											className="evs-detail-score-analysis__repeat-chart"
										/>
										<div className="evs-detail-score-analysis__donut-center">
											<div className="evs-detail-score-analysis__donut-center-label">
												Total
											</div>
											<div className="evs-detail-score-analysis__donut-center-value">
												{this.getRepeatTotal()}
											</div>
										</div>
										{(() => {
											const { paths, labels } = this.getRepeatLeaderPaths();
											return (
												<svg
													className="evs-detail-score-analysis__repeat-overlay"
													viewBox={`0 0 ${REPEAT_VIEWBOX.w} ${REPEAT_VIEWBOX.h}`}
													preserveAspectRatio="xMidYMid meet"
												>
													<defs>
														<circle id="repeat-dot" r={REPEAT_DOT_RADIUS} fill={REPEAT_CONNECTOR_COLOR} />
													</defs>
													{paths.map(({ d, key, dotX, dotY }) => (
														<g key={key}>
															<path
																d={d}
																fill="none"
																stroke={REPEAT_CONNECTOR_COLOR}
																strokeWidth="1"
															/>
															<use href="#repeat-dot" x={dotX} y={dotY} />
														</g>
													))}
													{labels.map(({ key, x, y, text, anchor }) => (
														<text
															key={key}
															x={x}
															y={y}
															textAnchor={anchor}
															dominantBaseline="middle"
															className="evs-detail-score-analysis__repeat-label"
														>
															{text}
														</text>
													))}
												</svg>
											);
										})()}
									</div>
								) : (
									<div className="evs-detail-score-analysis__repeat-no-data">
										No data available
									</div>
								)}
							</div>
							<div className="evs-detail-score-analysis__summary">
								<h6 className="evs-detail-score-analysis__evs-score-title mb-2">Campaign Summary</h6>
								{hasSummaryData ? (
									<div className="evs-detail-score-analysis__repeat-chart-wrap">
										<div
											id={this.summaryChartId}
											className="evs-detail-score-analysis__repeat-chart"
										/>
										<div className="evs-detail-score-analysis__donut-center">
											<div className="evs-detail-score-analysis__donut-center-label">
												Total
											</div>
											<div className="evs-detail-score-analysis__donut-center-value">
												{this.getSummaryTotal()}
											</div>
										</div>
										{(() => {
											const { paths, labels } = this.getSummaryLeaderPaths();
											return (
												<svg
													className="evs-detail-score-analysis__repeat-overlay"
													viewBox={`0 0 ${REPEAT_VIEWBOX.w} ${REPEAT_VIEWBOX.h}`}
													preserveAspectRatio="xMidYMid meet"
												>
													<defs>
														<circle id="summary-dot" r={REPEAT_DOT_RADIUS} fill={REPEAT_CONNECTOR_COLOR} />
													</defs>
													{paths.map(({ d, key, dotX, dotY }) => (
														<g key={key}>
															<path
																d={d}
																fill="none"
																stroke={REPEAT_CONNECTOR_COLOR}
																strokeWidth="1"
															/>
															<use href="#summary-dot" x={dotX} y={dotY} />
														</g>
													))}
													{labels.map(({ key, x, y, text, anchor }) => (
														<text
															key={key}
															x={x}
															y={y}
															textAnchor={anchor}
															dominantBaseline="middle"
															className="evs-detail-score-analysis__repeat-label"
														>
															{text}
														</text>
													))}
												</svg>
											);
										})()}
									</div>
								) : (
									<div className="evs-detail-score-analysis__repeat-no-data">
										No data available
									</div>
								)}
							</div>
						</div>
						
					</div>
				)}
			</div>
		);
	}
}
